import React, { useCallback } from "react";
import {
    Button, FormControl, FormErrorMessage, FormLabel, Input, Select,
    Switch,
    Text,
    NumberInput,
    NumberInputField,
    InputGroup,
    InputLeftAddon,
    useColorModeValue,
    Grid, GridItem,
    Box,
    Stack,
    Textarea,
    Flex,
} from "@chakra-ui/react";
import {
    MdSave,
    MdUpload,
} from "react-icons/md";
import { useNewUser } from "../hooks/useNewUser";
import { useDropzone } from "react-dropzone";
import InputMask from "react-input-mask";

type NewUserFormProps = Pick<useNewUser.type, "state" | "showModal" | "changeInput" | "changeAddressInput" | "changeContactInput" | "changeInputPermission" | "saveUser" | "displayInputError">;

const NewUserForm: React.FC<NewUserFormProps> = ({ state, showModal, changeInput, saveUser, displayInputError, changeAddressInput, changeContactInput, changeInputPermission }) => {

    const textColor = useColorModeValue("navy.700", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");

    const convertToBase64 = (file: File) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            changeInput({ type: "image", payload: reader.result as string });
        };
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            convertToBase64(acceptedFiles[0]);
        }
    }, []);


    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: "image/*",
        multiple: false,
    });

    return (
        <Box w="100%">
            <Box
                px="0px"
                mb="16px"
                mt="0px"
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={4}>
                    <Button
                        leftIcon={<MdSave />}
                        colorScheme="brand"
                        size={"sm"}
                        variant="solid"
                        isLoading={state.loading}
                        onClick={saveUser}
                    >
                        Salvar
                    </Button>
                </Stack>
            </Box>
            <Box w="100%">
                <Grid
                    templateColumns='repeat(6, 1fr)'
                    templateRows='repeat(4, 1fr)'
                    gap={4}
                    mb={4}
                >

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("nome").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Nome Completo<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o Nome do Usuario"
                                fontWeight="500"
                                defaultValue={state.user.nome}
                                size="md"
                                autoFocus={true}
                                onBlur={(e) => changeInput({ type: "nome", payload: e.target.value })}
                            />

                            {displayInputError("nome").error && (
                                <FormErrorMessage>{displayInputError("nome").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("cpf").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                CPF <Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o CPF"
                                fontWeight="500"
                                defaultValue={state.user.cpf}
                                size="md"
                                autoFocus={true}
                                onBlur={(e) => changeInput({ type: "cpf", payload: e.target.value })}
                                as={InputMask}
                                mask={"999.999.999-99"}
                            />

                            {displayInputError("cpf").error && (
                                <FormErrorMessage>{displayInputError("cpf").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("rg").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                RG 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o RG"
                                fontWeight="500"
                                defaultValue={state.user.rg}
                                size="md"
                                autoFocus={true}
                                onBlur={(e) => changeInput({ type: "rg", payload: e.target.value })}
                                as={InputMask}
                                mask={"99.999.999-9"}
                            />

                            {displayInputError("rg").error && (
                                <FormErrorMessage>{displayInputError("rg").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("nascimento").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Data de nascimento
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="date"
                                placeholder="Preencha a data de nascimento"
                                fontWeight="500"
                                defaultValue={state.user.nascimento?.split('T')[0]}
                                size="md"
                                onBlur={(e) => changeInput({ type: "nascimento", payload: new Date(e.target.value).toISOString() })}
                            />

                            {displayInputError("nascimento").error && (
                                <FormErrorMessage>{displayInputError("nascimento").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={2} >
                        <FormLabel
                            display="flex"
                            ms="4px"
                            fontSize="sm"
                            fontWeight="500"
                            color={textColor}
                            mb="8px"
                        >
                            Foto
                        </FormLabel>
                        <Flex
                            align="center"
                            justify="center"
                            bg="gray.100"
                            mt={4}
                            border="1px dashed"
                            borderColor="secondaryGray.100"
                            borderRadius="16px"
                            minH="100%"
                            cursor="pointer"
                            {...getRootProps({ className: "dropzone" })}
                        >
                            <input {...getInputProps()} />
                            {state?.user?.image ? (
                                <div>
                                    <img src={state?.user?.image} alt="Preview" className="object-cover rounded-lg" />
                                </div>
                            ) : (
                                <Button variant="no-effects" leftIcon={<MdUpload />}>
                                    Encaminhar Foto
                                </Button>
                            )}
                        </Flex>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioContato.cargo").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Cargo
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o Cargo"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioContato[0]?.cargo}
                                size="md"
                                onBlur={(e) => changeContactInput({ type: "cargo", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioContato.cargo").error && (
                                <FormErrorMessage>{displayInputError("UsuarioContato.cargo").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("dataAdmissao").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Data de admissão
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="date"
                                placeholder="Preencha a data de admissão"
                                fontWeight="500"
                                defaultValue={state.user.dataAdmissao?.split('T')[0]}
                                size="md"
                                onBlur={(e) => changeInput({ type: "dataAdmissao", payload: new Date(e.target.value).toISOString() })}
                            />

                            {displayInputError("dataAdmissao").error && (
                                <FormErrorMessage>{displayInputError("dataAdmissao").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("pispasep").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                PIS/PASEP
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o PIS/PASEP"
                                fontWeight="500"
                                defaultValue={state.user.pispasep}
                                size="md"
                                onBlur={(e) => changeInput({ type: "pispasep", payload: e.target.value })}
                                as={InputMask}
                                mask={"999.99999.99-9"}
                            />

                            {displayInputError("pispasep").error && (
                                <FormErrorMessage>{displayInputError("pispasep").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("ctps").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                CTPS
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o CTPS"
                                fontWeight="500"
                                defaultValue={state.user.ctps}
                                size="md"
                                onBlur={(e) => changeInput({ type: "ctps", payload: e.target.value })}
                                as={InputMask}
                                mask={"9999999/9999"}
                            />

                            {displayInputError("ctps").error && (
                                <FormErrorMessage>{displayInputError("ctps").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("codigo").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Código
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o Código"
                                fontWeight="500"
                                defaultValue={state.user.codigo}
                                size="md"
                                onBlur={(e) => changeInput({ type: "codigo", payload: e.target.value })}
                            />

                            {displayInputError("codigo").error && (
                                <FormErrorMessage>{displayInputError("codigo").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("salario").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Salário
                            </FormLabel>
                            <InputGroup>
                                <InputLeftAddon>R$</InputLeftAddon>
                                <NumberInput
                                    precision={2}
                                    min={0}
                                    isRequired={true}
                                    fontSize="sm"
                                    ms={{ base: "0px", md: "0px" }}
                                    placeholder="Preencha o Salário"
                                    fontWeight="500"
                                    defaultValue={state.user.salario}
                                    size="md"
                                    onBlur={(e) => changeInput({ type: "salario", payload: e.target.value })}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </InputGroup>

                            {displayInputError("salario").error && (
                                <FormErrorMessage>{displayInputError("salario").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("email").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                E-mail<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="email"
                                placeholder="Preencha o E-mail"
                                fontWeight="500"
                                defaultValue={state.user.email}
                                size="md"
                                onBlur={(e) => changeInput({ type: "email", payload: e.target.value })}
                            />

                            {displayInputError("email").error && (
                                <FormErrorMessage>{displayInputError("email").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("senha").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Senha<Text color={brandStars}>*</Text>
                            </FormLabel>

                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="password"
                                placeholder="Preencha a Senha"
                                fontWeight="500"
                                defaultValue={state.user.senha}
                                size="md"
                                onBlur={(e) => changeInput({ type: "senha", payload: e.target.value })}
                            />

                            {displayInputError("senha").error && (
                                <FormErrorMessage>{displayInputError("senha").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} />

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("idEmpresa").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Empresa
                            </FormLabel>

                            <Select
                                size="md"
                                defaultValue={state.user?.idEmpresa}
                                onChange={(e) => changeInput({ type: "idEmpresa", payload: e.target.value })}
                                placeholder={'Selecione a empresa'}
                            >
                                {state.companyList?.map((item) => (
                                    <option key={item.id} value={item?.id}>{item?.nomeFantasia}</option>
                                ))}
                            </Select>

                            {displayInputError("idEmpresa").error && (
                                <FormErrorMessage>{displayInputError("idEmpresa").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Ativo
                            </FormLabel>

                            <Switch
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="checkbox"
                                fontWeight="500"
                                size="md"
                                isChecked={state.user.ativo}
                                onChange={(e) => changeInput({ type: "ativo", payload: e.target.checked })}
                            />
                        </FormControl>
                    </GridItem>
                </Grid>

                <Text
                    mt={8}
                    fontSize={18}
                    color={"#422AFB"}
                    fontWeight={"bold"}
                    mb={4}
                >
                    Contato
                </Text>

                <Grid
                    templateColumns='repeat(6, 1fr)'
                    templateRows='repeat(1, 1fr)'
                    gap={4}
                >
                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioContato.nome").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Nome
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o Nome de contato"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioContato[0]?.nome}
                                size="md"
                                onBlur={(e) => changeContactInput({ type: "nome", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioContato.nome").error && (
                                <FormErrorMessage>{displayInputError("UsuarioContato.nome").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioContato.telefone").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Telefone<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o Numero de Telefone"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioContato[0]?.telefone}
                                size="md"
                                onBlur={(e) => changeContactInput({ type: "telefone", payload: e.target.value })}
                                as={InputMask}
                                mask={"(99) 99999-9999"}
                            />

                            {displayInputError("UsuarioContato.telefone").error && (
                                <FormErrorMessage>{displayInputError("UsuarioContato.telefone").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioContato.email").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                E-mail 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="email"
                                placeholder="Preencha o E-mail de contato"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioContato[0]?.email}
                                size="md"
                                onBlur={(e) => changeContactInput({ type: "email", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioContato.email").error && (
                                <FormErrorMessage>{displayInputError("UsuarioContato.email").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>
                </Grid>

                <Text
                    mt={8}
                    fontSize={18}
                    color={"#422AFB"}
                    fontWeight={"bold"}
                    mb={4}
                >
                    Endereço
                </Text>

                <Grid
                    templateColumns='repeat(6, 1fr)'
                    templateRows='repeat(4, 1fr)'
                    gap={4}
                >
                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.cep").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                CEP
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o cep"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.cep}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "cep", payload: e.target.value })}
                                as={InputMask}
                                mask={"99999-999"}
                            />

                            {displayInputError("UsuarioEndereco.cep").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.cep").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.estado").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                UF
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o UF"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.estado}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "estado", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.estado").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.estado").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.cidade").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Cidade 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o nome da cidade"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.cidade}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "cidade", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.cidade").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.cidade").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.bairro").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Bairro 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o nome do bairro"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.bairro}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "bairro", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.bairro").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.bairro").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.local").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Endereço 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o endereço"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.local}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "local", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.local").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.local").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.numero").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Numero 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o numero"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.numero}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "numero", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.numero").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.numero").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={3} rowSpan={1} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.complemento").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Complemento 
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha o complemento"
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.complemento}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "complemento", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.complemento").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.complemento").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={6} rowSpan={2} >
                        <FormControl isInvalid={displayInputError("UsuarioEndereco.observacao").error}>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Observação 
                            </FormLabel>
                            <Textarea
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                fontWeight="500"
                                defaultValue={state.user.UsuarioEndereco[0]?.observacao}
                                size="md"
                                onBlur={(e) => changeAddressInput({ type: "observacao", payload: e.target.value })}
                            />

                            {displayInputError("UsuarioEndereco.observacao").error && (
                                <FormErrorMessage>{displayInputError("UsuarioEndereco.observacao").errorMessage}</FormErrorMessage>
                            )}
                        </FormControl>
                    </GridItem>
                </Grid>

                {/* <Accordion allowToggle>
                        <AccordionItem>
                            <AccordionButton>
                                <Box as='span' flex='1' textAlign='left'>
                                    <Text
                                        fontSize={18}
                                        color={"#422AFB"}
                                        fontWeight={"bold"}
                                    >
                                        Permissionamento
                                    </Text>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                <Grid
                                    templateColumns='repeat(8, 1fr)'
                                    templateRows={`repeat(${Object.entries(state.permission).length / 2}, 1fr)`}
                                    gap={4}>
                                    {Object.entries(state.permission).map(([key, value]) => (
                                        <GridItem colSpan={1} rowSpan={1} key={key}>
                                            <FormLabel
                                                display="flex"
                                                ms="4px"
                                                fontSize="sm"
                                                fontWeight="500"
                                                color={textColor}
                                                mb="8px"
                                            >
                                                {getDisplayNameByKey(key)}
                                            </FormLabel>

                                            {Object.entries((value as any).rules).map(([ruleKey, ruleValue]) => (
                                                <FormControl key={key + ruleKey} >
                                                    <Checkbox 
                                                    size='md' 
                                                    colorScheme='green' 
                                                    isChecked={ruleValue as boolean}
                                                    onChange={(e) => changeInputPermission(key, ruleKey as any, e.target.checked)}>
                                                        {getDisplayNameByKeyRule(ruleKey)}
                                                    </Checkbox>
                                                </FormControl>
                                            ))}
                                        </GridItem>
                                    ))}
                                </Grid>
                            </AccordionPanel>
                        </AccordionItem>

                    </Accordion> */}
            </Box>
        </Box>
    );
}

export default NewUserForm;
